import { useState } from "react";
import { CapAwareButton } from "../../components/CapAwareButton";

import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { useLang } from "../../contexts/LangContext";
import {
  useTelegramStatus, useWhatsAppStatus,
  useTelegramMutations, useWhatsAppMutations,
  useRemindersMutations, useReminderPreview,
  useProfile,
} from "../../hooks/queries";
import { toast } from "sonner";
import { MessageSquare, Loader2, CheckCircle2, ExternalLink, Unlink, Phone, BellRing, Lock, ArrowRight, LifeBuoy } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { tx } from "../../i18n";
import { api } from "../../lib/api";
import { resolvePriceForUser, formatPrice } from "../../lib/pricingResolver";
import { WhatsAppActivateSheet } from "../../components/WhatsAppActivateSheet";

export default function Integrations() {
  const { lang } = useLang();
  const { data: status, isFetching: tgFetching, refetch: refetchTg } = useTelegramStatus();
  const { data: waStatus, isFetching: waFetching, refetch: refetchWa } = useWhatsAppStatus();
  const { data: profile } = useProfile();
  const userCurrency = String(profile?.currency || "USD").toUpperCase();
  const tgMut = useTelegramMutations();
  const waMut = useWhatsAppMutations();
  const remindersMut = useRemindersMutations();
  const { data: reminderPreview } = useReminderPreview();
  const refreshing = tgFetching || waFetching;
  const load = () => { refetchTg(); refetchWa(); };

  const [code, setCode] = useState("");
  const [waCode, setWaCode] = useState("");
  // iter-170t — Activate +WhatsApp BottomSheet (in-place pitch instead
  // of a hard navigate to /billing).
  const [waSheetOpen, setWaSheetOpen] = useState(false);

  const submitCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      return toast.error(tx(lang, "Enter a 6-digit code", "Ingresa un código de 6 dígitos", "Ingresa un código de 6 dígitos"));
    }
    try {
      await tgMut.link.mutateAsync(code);
      toast.success(tx(lang, "Connected!", "¡Conectado!", "¡Conectado!"));
      setCode("");
    } catch (e) { console.error("telegram link failed", e); /* onError toast already fired */ }
  };

  const unlink = async () => {
    try {
      await tgMut.unlink.mutateAsync();
      toast.success(tx(lang, "Disconnected", "Desconectado", "Desconectado"));
    } catch (e) { console.error("telegram unlink failed", e); /* onError toast already fired */ }
  };

  const submitWhatsAppCode = async () => {
    if (!/^\d{6}$/.test(waCode)) {
      return toast.error(tx(lang, "Enter a 6-digit code", "Ingresa un código de 6 dígitos", "Ingresa un código de 6 dígitos"));
    }
    try {
      await waMut.link.mutateAsync(waCode);
      toast.success(tx(lang, "WhatsApp connected!", "¡WhatsApp conectado!", "¡WhatsApp conectado!"));
      setWaCode("");
    } catch (e) {
      // The global onError handler in queries.js already shows a
      // translated toast via formatApiError (covers 402 / 404 / 401
      // and any future codes uniformly). Nothing extra to do here.
      console.error("whatsapp link failed", e);
    }
  };

  const unlinkWhatsApp = async () => {
    try {
      await waMut.unlink.mutateAsync();
      toast.success(tx(lang, "Disconnected", "Desconectado", "Desconectado"));
    } catch (e) { console.error("whatsapp unlink failed", e); /* onError toast already fired */ }
  };

  const sendReminderNow = async () => {
    try {
      const result = await remindersMut.sendNow.mutateAsync();
      if (result?.sent) {
        const channels = (result.channels || []).filter((c) => c.ok).map((c) => c.channel).join(", ");
        toast.success(
          tx(lang, `Reminder sent via ${channels}`, `Recordatorio enviado por ${channels}`, `Recordatorio enviado por ${channels}`)
        );
      } else if (result?.reason === "nothing_to_remind") {
        toast.info(
          tx(lang, "Nothing to remind — no upcoming payments or receivables.", "Nada que recordar — no tienes pagos ni cobros próximos.", "Nada que recordar — no tienes pagos ni cobros próximos.")
        );
      } else if (result?.no_link_channels) {
        toast.error(
          tx(lang, "Connect Telegram or WhatsApp first.", "Conecta Telegram o WhatsApp primero.", "Conecta Telegram o WhatsApp primero.")
        );
      } else {
        toast.error(tx(lang, "Could not send reminder", "No se pudo enviar el recordatorio", "No se pudo enviar el recordatorio"));
      }
    } catch (e) {
      console.error("send reminder failed", e);
    }
  };

  const loading = tgMut.link.isPending;
  const waLoading = waMut.link.isPending;

  const waLinked = waStatus?.linked;

  const botUsername = status?.bot_username;
  const botUrl = botUsername ? `https://t.me/${botUsername}` : null;
  const botError = status?.bot_error;
  const botErrorDetail = status?.bot_error_detail;
  const linked = status?.linked;
  const linkInfo = status?.link;

  return (
    <Layout>
      <div className="max-w-2xl" data-testid="integrations-page">
        <PageHeader
          testid="integrations-header"
          eyebrow={tx(lang, "Bots & channels", "Bots y canales", "Bots y canales")}
          title={tx(lang, "Integrations", "Integraciones", "Integraciones")}
          subtitle={tx(lang, "Connect messaging channels to log movements from anywhere.", "Conecta tus canales de mensajería para registrar movimientos desde donde estés.", "Conecta tus canales de mensajería para registrar movimientos desde donde estés.")}
        />

        {/* iter-170x — Connected bots summary so the user sees at a
            glance which channels are live before scrolling through the
            two per-channel cards below. */}
        {(linked || waLinked) && (
          <Card
            className="mt-4 p-4 rounded-2xl border-emerald-200 bg-emerald-50/40"
            data-testid="connected-bots-summary"
          >
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {tx(lang, "Connected bots", "Bots conectados", "Bots conectados")}
            </h3>
            <ul className="space-y-1.5 text-sm" data-testid="connected-bots-list">
              {(status?.links || (linked ? [linkInfo] : [])).map((tgLink, idx) => (
                <li
                  key={`tg-${tgLink?.chat_id || idx}`}
                  className="flex items-center gap-2 flex-wrap"
                  data-testid={`connected-bot-telegram-${idx}`}
                >
                  <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">Telegram</Badge>
                  <span className="text-slate-700">
                    {tgLink?.first_name && <span className="font-semibold">{tgLink.first_name}</span>}
                    {tgLink?.username && <span className="text-slate-500"> @{tgLink.username}</span>}
                  </span>
                  {tgLink?.linked_at && (
                    <span className="text-[11px] text-slate-500">
                      · {tx(lang, "since", "desde", "desde")} {new Date(tgLink.linked_at).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
              {(waStatus?.links || (waLinked ? [waStatus?.link] : [])).map((waLink, idx) => (
                <li
                  key={`wa-${waLink?.phone_e164 || idx}`}
                  className="flex items-center gap-2 flex-wrap"
                  data-testid={`connected-bot-whatsapp-${idx}`}
                >
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">WhatsApp</Badge>
                  <span className="text-slate-700">
                    {waLink?.phone_e164 && (
                      <span className="font-mono font-semibold">{waLink.phone_e164}</span>
                    )}
                  </span>
                  {waLink?.linked_at && (
                    <span className="text-[11px] text-slate-500">
                      · {tx(lang, "since", "desde", "desde")} {new Date(waLink.linked_at).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {/* iter-170y — Show the per-channel cap usage so the user
                knows how many more they can add. */}
            <div className="mt-2 text-[10px] text-slate-500 flex gap-3 flex-wrap" data-testid="connected-bots-caps">
              {typeof status?.lines_allowed === "number" && (
                <span>
                  Telegram: {status.lines_used || 0}/{status.lines_allowed >= 999 ? "∞" : status.lines_allowed}
                </span>
              )}
              {typeof waStatus?.lines_allowed === "number" && (
                <span>
                  WhatsApp: {waStatus.lines_used || 0}/{waStatus.lines_allowed >= 999 ? "∞" : waStatus.lines_allowed}
                </span>
              )}
            </div>
          </Card>
        )}

        {/* Telegram */}
        <Card className="mt-2 p-6 rounded-2xl border-slate-200 fade-up" data-testid="telegram-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-xl font-bold text-slate-900">Telegram</h2>
                  {linked && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="telegram-linked-badge">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {tx(lang, "Connected", "Conectado", "Conectado")}
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  {botUsername ? `@${botUsername}` : "Lumfi Bot"}
                </div>
              </div>
            </div>
            {linked && (
              <Button
                data-testid="telegram-unlink-btn"
                variant="outline"
                size="sm"
                onClick={unlink}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Unlink className="h-4 w-4" /> {tx(lang, "Disconnect", "Desconectar", "Desconectar")}
              </Button>
            )}
          </div>

          {/* iter-170y — Show the activate flow when the user still
              has room for another line. ``!linked`` alone hid the
              form for multi-bot users. */}
          {((status?.lines_used ?? 0) < (status?.lines_allowed ?? 1)) && (
            <div className="mt-6 space-y-5">
              {linked && (
                <div className="text-xs text-slate-500 italic" data-testid="telegram-multi-line-hint">
                  {tx(lang,
                    `You have ${status?.lines_used || 0} of ${status?.lines_allowed} lines used — connect another below.`,
                    `Tienes ${status?.lines_used || 0} de ${status?.lines_allowed} líneas usadas — conecta otra abajo.`,
                    `Você tem ${status?.lines_used || 0} de ${status?.lines_allowed} linhas usadas — conecte outra abaixo.`)}
                </div>
              )}
              <ol className="text-sm text-slate-700 space-y-3 list-decimal list-inside">
                <li>
                  {tx(lang, "Open the bot on Telegram: ", "Abre el bot en Telegram: ", "Abre el bot en Telegram: ")}
                  {botUrl ? (
                    <a
                      href={botUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="telegram-bot-link"
                      className="text-sky-600 font-semibold inline-flex items-center gap-1"
                    >
                      @{botUsername} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : botError ? (
                    <span data-testid="telegram-bot-error" className="text-rose-600 font-semibold">
                      {botError === "no_token"
                        ? (tx(lang, "Bot token not configured — contact the admin.", "Token del bot no configurado — contacta al admin.", "Token del bot no configurado — contacta al admin."))
                        : botError === "unreachable"
                          ? (tx(lang, "Cannot reach Telegram. Retry in a moment.", "No se puede contactar a Telegram. Reintenta en un momento.", "No se puede contactar a Telegram. Reintenta en un momento."))
                          : botError === "telegram_api_error"
                            ? tx(lang,
                                `Telegram rejected the token${botErrorDetail ? ` (${botErrorDetail})` : ""}. Ask the admin to verify.`,
                                `Telegram rechazó el token${botErrorDetail ? ` (${botErrorDetail})` : ""}. Pídele al admin que verifique.`,
                                `Telegram rejeitou o token${botErrorDetail ? ` (${botErrorDetail})` : ""}. Peça ao admin para verificar.`,
                              )
                            : (tx(lang, "Temporary bot error.", "Error temporal con el bot.", "Error temporal con el bot."))}
                      {" "}
                      <button /* design-system: ALLOW legacy-todo */
                        type="button"
                        onClick={() => refetchTg()}
                        className="underline underline-offset-2 hover:text-rose-800 font-medium"
                        data-testid="telegram-retry-btn"
                      >
                        {tx(lang, "Retry", "Reintentar", "Reintentar")}
                      </button>
                    </span>
                  ) : (
                    <span className="text-slate-500 inline-flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {tx(lang, "Loading bot…", "Cargando bot…", "Cargando bot…")}
                    </span>
                  )}
                </li>
                <li>{tx(lang, "Send the command ", "Envía el comando ", "Envía el comando ")}<code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">/start</code></li>
                <li>{tx(lang, "Paste here the 6-digit code you receive.", "Pega aquí el código de 6 dígitos que recibas.", "Pega aquí el código de 6 dígitos que recibas.")}</li>
              </ol>

              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {tx(lang, "Linking code", "Código de vinculación", "Código de vinculación")}
                  </Label>
                  <Input
                    data-testid="telegram-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1.5 h-12 text-lg font-mono tracking-[0.4em] text-center rounded-xl"
                  />
                </div>
                <CapAwareButton
                  resourceKey="connected_bots"
                  featureLabel={tx(lang, "connected bots", "bots conectados", "bots conectados")}
                  isEs={lang === "es"}
                  data-testid="telegram-link-btn"
                  onClick={submitCode}
                  disabled={loading || code.length !== 6}
                  className="bg-emerald-600 hover:bg-emerald-700 h-12 rounded-xl gap-2 font-semibold"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {tx(lang, "Connect", "Conectar", "Conectar")}
                </CapAwareButton>
              </div>
            </div>
          )}

          {linked && linkInfo && (
            <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">
                  {linkInfo.first_name && <span className="font-semibold text-slate-900">{linkInfo.first_name}</span>}
                  {linkInfo.username && <span className="text-slate-500"> (@{linkInfo.username})</span>}
                </span>
                <span className="text-xs text-slate-500">
                  {tx(lang, "Linked ", "Conectado ", "Conectado ")}
                  {linkInfo.linked_at ? new Date(linkInfo.linked_at).toLocaleString() : ""}
                </span>
              </div>
              <p className="mt-3 text-slate-600">
                {tx(lang, "Send any message to the bot in plain language and it will be saved automatically using your chat memory.", "Envía cualquier movimiento al bot en lenguaje natural y se guardará automáticamente con tu memoria de chat.", "Envía cualquier movimiento al bot en lenguaje natural y se guardará automáticamente con tu memoria de chat.")}
              </p>
            </div>
          )}
        </Card>

        {/* WhatsApp */}
        <Card className="mt-5 p-6 rounded-2xl border-slate-200 fade-up-1" data-testid="whatsapp-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Phone className="h-6 w-6" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-xl font-bold text-slate-900">WhatsApp</h2>
                  {waStatus?.configured && (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">
                      WhatsApp activo
                    </Badge>
                  )}
                  {waLinked && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="whatsapp-linked-badge">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {tx(lang, "Connected", "Conectado", "Conectado")}
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  {waStatus?.from_number ? `Desde: ${waStatus.from_number}` : "WhatsApp"}
                </div>
              </div>
            </div>
            {waLinked && (
              <Button
                data-testid="whatsapp-unlink-btn"
                variant="outline"
                size="sm"
                onClick={unlinkWhatsApp}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Unlink className="h-4 w-4" /> {tx(lang, "Disconnect", "Desconectar", "Desconectar")}
              </Button>
            )}
          </div>

          {!waLinked && waStatus && waStatus.entitled === false && (
            <div className="mt-6" data-testid="whatsapp-upsell-card">
              <div className="rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">
                        {tx(lang, "Add-on required", "Addon requerido", "Addon requerido")}
                      </h3>
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-white text-[10px]">
                        +WhatsApp
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {tx(lang, "The WhatsApp bot is part of the +WhatsApp add-on. Activate it to link your number and log movements via chat.", "El bot de WhatsApp es parte del addon +WhatsApp. Actívalo para vincular tu número y registrar movimientos por chat.", "El bot de WhatsApp es parte del addon +WhatsApp. Actívalo para vincular tu número y registrar movimientos por chat.")}
                    </p>
                    {waStatus.addon_required_plan && (() => {
                      const pricing = waStatus.addon_required_plan.pricing || {};
                      // iter-170s — Resolve by user's profile currency,
                      // not by "whatever Mongo returned first" (which
                      // was ARS and confused every non-AR user). If no
                      // price exists in the user's currency AND there's
                      // no USD fallback, expose a "Contactar soporte"
                      // CTA so the operator can request the addon at a
                      // negotiated rate instead of silently showing
                      // a wrong price.
                      const resolved = resolvePriceForUser(pricing, userCurrency);
                      const priceLabel = resolved
                        ? formatPrice(resolved.entry.amount, resolved.entry.currency, lang)
                        : null;
                      const planName =
                        waStatus.addon_required_plan.name ||
                        waStatus.addon_required_plan.id ||
                        "WhatsApp add-on";
                      const onContactSupport = async () => {
                        const body = tx(lang,
                          `Hi, I'd like to activate the ${planName} add-on but I don't see a price in ${userCurrency}. Can someone reach out with a quote?`,
                          `Hola, quiero activar el addon ${planName} pero no veo un precio en ${userCurrency}. ¿Pueden contactarme con una cotización?`,
                          `Olá, gostaria de ativar o addon ${planName} mas não vejo um preço em ${userCurrency}. Podem entrar em contato com uma cotação?`);
                        try {
                          await api.post("/support/chat/send", { text: body });
                          toast.success(
                            tx(lang,
                              "Message sent — the team will reply in support chat.",
                              "Mensaje enviado · el equipo te responderá por el chat de soporte.",
                              "Mensagem enviada · a equipe responderá no chat de suporte."),
                          );
                        } catch (err) {
                          toast.error(
                            tx(lang, "Couldn't send the message. Try again.",
                              "No se pudo enviar el mensaje. Intenta de nuevo.",
                              "Não foi possível enviar a mensagem. Tente novamente."),
                          );
                        }
                      };
                      return (
                        <div className="mt-4 flex items-center gap-3 flex-wrap">
                          {resolved ? (
                            <Button
                              type="button"
                              onClick={() => setWaSheetOpen(true)}
                              data-testid="whatsapp-upsell-cta"
                              className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
                            >
                              {tx(lang, "Activate +WhatsApp", "Activar +WhatsApp", "Activar +WhatsApp")}
                              {priceLabel && (
                                <span className="font-mono text-xs opacity-90">
                                  · {priceLabel}/{tx(lang, "mo", "mes", "mês")}
                                </span>
                              )}
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              onClick={onContactSupport}
                              data-testid="whatsapp-upsell-contact-support"
                              className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
                            >
                              <LifeBuoy className="h-4 w-4" />
                              {tx(lang,
                                `Request +WhatsApp in ${userCurrency}`,
                                `Solicitar +WhatsApp en ${userCurrency}`,
                                `Solicitar +WhatsApp em ${userCurrency}`)}
                            </Button>
                          )}
                          {/* Manual re-check button — admins frequently grant
                              a +WhatsApp bonus while the user is on this page.
                              Without this, the user sees a stale "addon
                              required" banner until the next focus/refresh. */}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => refetchWa()}
                            disabled={waFetching}
                            data-testid="whatsapp-recheck-btn"
                            className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            {waFetching
                              ? (tx(lang, "Checking…", "Verificando…", "Verificando…"))
                              : (tx(lang, "I have the bonus, re-check", "Ya tengo el bono, re-verificar", "Ya tengo el bono, re-verificar"))}
                          </Button>
                          <span className="text-xs text-slate-500">
                            {resolved
                              ? tx(lang, "You can cancel anytime from Billing.",
                                  "Puedes cancelarlo cuando quieras desde Billing.",
                                  "Você pode cancelar quando quiser em Billing.")
                              : tx(lang,
                                  `No price in ${userCurrency} yet — the team will follow up.`,
                                  `Aún no tenemos precio en ${userCurrency} — el equipo te contactará.`,
                                  `Ainda não temos preço em ${userCurrency} — a equipe entrará em contato.`)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {((waStatus?.lines_used ?? 0) < (waStatus?.lines_allowed ?? 1)) && waStatus?.entitled !== false && (
            <div className="mt-6 space-y-5">
              <ol className="text-sm text-slate-700 space-y-3 list-decimal list-inside">
                <li>
                  {tx(lang, "Open WhatsApp and send ", "Abre WhatsApp y envía ", "Abre WhatsApp y envía ")}
                  <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">start</code>
                  {tx(lang, " to the number ", " al número ", " al número ")}
                  <a
                    href={`https://wa.me/${(waStatus?.from_number || "").replace(/\D/g, "")}?text=start`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-bot-link"
                    className="text-emerald-700 font-semibold inline-flex items-center gap-1"
                  >
                    {waStatus?.from_number || ""} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
                <li>{tx(lang, "You'll receive a 6-digit code. Paste it below.", "Recibirás un código de 6 dígitos. Pégalo aquí abajo.", "Recibirás un código de 6 dígitos. Pégalo aquí abajo.")}</li>
              </ol>

              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {tx(lang, "Linking code", "Código de vinculación", "Código de vinculación")}
                  </Label>
                  <Input
                    data-testid="whatsapp-code-input"
                    value={waCode}
                    onChange={(e) => setWaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1.5 h-12 text-lg font-mono tracking-[0.4em] text-center rounded-xl"
                  />
                </div>
                <Button
                  data-testid="whatsapp-link-btn"
                  onClick={submitWhatsAppCode}
                  disabled={waLoading || waCode.length !== 6}
                  className="bg-emerald-600 hover:bg-emerald-700 h-12 rounded-xl gap-2 font-semibold"
                >
                  {waLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {tx(lang, "Connect", "Conectar", "Conectar")}
                </Button>
              </div>

              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                <span>⚠️</span>
                <div>
                  {tx(lang, "If your account doesn't have WhatsApp enabled yet, ping the Lumfi team via the support chat and we'll set it up.", "Si tu cuenta aún no tiene WhatsApp activo, escribele al equipo de Lumfi por el chat de soporte y te lo habilitamos.", "Si tu cuenta aún no tiene WhatsApp activo, escribele al equipo de Lumfi por el chat de soporte y te lo habilitamos.")}
                </div>
              </div>
            </div>
          )}

          {waLinked && waStatus?.link && (
            <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono text-slate-900">{waStatus.link.phone}</span>
                <span className="text-xs text-slate-500">
                  {tx(lang, "Linked ", "Conectado ", "Conectado ")}
                  {waStatus.link.linked_at ? new Date(waStatus.link.linked_at).toLocaleString() : ""}
                </span>
              </div>
              <p className="mt-3 text-slate-600">
                {tx(lang, "Send any message via WhatsApp and it'll be saved automatically.", "Envía cualquier movimiento por WhatsApp y se guardará automáticamente.", "Envía cualquier movimiento por WhatsApp y se guardará automáticamente.")}
              </p>
            </div>
          )}
        </Card>

        {/* Daily reminders */}
        <Card className="mt-5 p-6 rounded-2xl border-slate-200 fade-up-2" data-testid="reminders-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <BellRing className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900">
                  {tx(lang, "Daily reminders", "Recordatorios diarios", "Recordatorios diarios")}
                </h2>
                <div className="text-sm text-slate-500">
                  {tx(lang, "Auto summary of upcoming payments and receivables via Telegram/WhatsApp.", "Resumen automático de pagos y cobros próximos por Telegram/WhatsApp.", "Resumen automático de pagos y cobros próximos por Telegram/WhatsApp.")}
                </div>
              </div>
            </div>
            <Button
              data-testid="reminders-send-now-btn"
              onClick={sendReminderNow}
              disabled={remindersMut.sendNow.isPending}
              className="bg-slate-900 hover:bg-slate-800 rounded-xl gap-2"
            >
              {remindersMut.sendNow.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BellRing className="h-4 w-4" />
              )}
              {tx(lang, "Send one now", "Enviarme uno ahora", "Enviarme uno ahora")}
            </Button>
          </div>

          {reminderPreview && (
            <div className="mt-5" data-testid="reminders-preview">
              {reminderPreview.has_content ? (
                <pre className="whitespace-pre-wrap text-sm leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-4 text-slate-900 font-sans">
                  {reminderPreview.body}
                </pre>
              ) : (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-4">
                  {tx(lang, "No upcoming payments or receivables to remind you about. Good news!", "No hay pagos ni cobros próximos para recordarte. ¡Buenas noticias!", "No hay pagos ni cobros próximos para recordarte. ¡Buenas noticias!")}
                </div>
              )}
            </div>
          )}

          {!linked && !waLinked && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {tx(lang, "Connect Telegram or WhatsApp above to start receiving reminders.", "Conecta Telegram o WhatsApp arriba para empezar a recibir recordatorios.", "Conecta Telegram o WhatsApp arriba para empezar a recibir recordatorios.")}
            </div>
          )}
        </Card>
      </div>
      {/* iter-170t — In-place pitch sheet for the +WhatsApp addon. */}
      <WhatsAppActivateSheet
        open={waSheetOpen}
        onOpenChange={setWaSheetOpen}
        lang={lang}
        planId={waStatus?.addon_required_plan?.id}
        planName={waStatus?.addon_required_plan?.name}
        priceEntry={
          waStatus?.addon_required_plan
            ? (resolvePriceForUser(waStatus.addon_required_plan.pricing || {}, userCurrency)?.entry || null)
            : null
        }
      />
    </Layout>
  );
}
